import {
  pure,
  compose,
  withPropsOnChange,
  withState,
  withHandlers,
  mapProps,
} from 'recompose';
import {
  pathOr,
  isNil,
  converge,
  filter,
  identity,
  unapply,
  pipe,
} from 'ramda';

import { Input as Inp } from './Input';

const getErrors = pipe(unapply(identity), filter(identity));
const makeValidation = (rules) => converge(getErrors, rules);
const validate = (value, { error, validateRules = [] } = {}) => {
  const errors = error || makeValidation(validateRules)(value);

  return errors.length ? errors[0] : false;
};

const setDefaultState = ({ clear, value, ...rest }, isFocused = false) => ({
  showClear: clear && !!value,
  isFocused,
  value: isNil(value) ? '' : value,
  error: validate(value, rest),
});

const enhance = compose(
  withHandlers(() => {
    let ref;

    return {
      setRef: () => (value) => {
        ref = value;
      },
      focus: () => () => {
        if (ref) {
          ref.focus();
        }
      },
    };
  }),
  withState('state', 'setState', setDefaultState),
  withPropsOnChange(['clear', 'value'], ({ state, setState, ...rest }) => {
    setState(setDefaultState(rest, state && state.isFocused));

    return {};
  }),
  withPropsOnChange(['error'], ({ state, setState, value, ...rest }) => {
    setState({
      ...state,
      error: validate(value, rest),
    });

    return {};
  }),
  withHandlers({
    onChange: ({ clear, onChange, state, setState, ...rest }) => (e) => {
      const defaultRegExp = new RegExp(/[^a-zA-Z\d-]/gi);
      const hasPassedRegexp = rest.regexp instanceof RegExp && rest.regexp;
      const regExp = hasPassedRegexp ? rest.regexp : defaultRegExp;
      const shouldFilterValue = hasPassedRegexp || rest.filterEnable;

      const rawValue = pathOr('', ['target', 'value'], e);
      const value = shouldFilterValue ? rawValue.replace(regExp, '') : rawValue;

      onChange && onChange(value);

      setState({
        ...state,
        showClear: clear && !!value,
        value,
        error: validate(value, rest),
      });
    },
    onFocus: ({ disabled, readOnly, onFocus, state, setState }) => (e) => {
      if (disabled) {
        return;
      }

      if (readOnly) {
        onFocus && onFocus(e);

        return;
      }

      setState({ ...state, isFocused: true });
    },
    onBlur: ({ onBlur, state, setState }) => (e) => {
      onBlur && onBlur(e);

      setState({ ...state, isFocused: false });
    },
    // eslint-disable-next-line no-unused-vars
    handleClear: ({ onChange, setState, ...rest }) => () => {
      onChange && onChange('');

      setState({
        showClear: false,
        isFocused: false,
        value: '',
        error: validate('', rest),
      });
    },
  }),
  mapProps(({ clear, state, setState, ...rest }) => ({
    ...rest,
    ...state,
  })),
  pure,
);

export const Input = enhance(Inp);
